import { useState, useEffect } from 'react';
import { Star, MessageSquare, User, Calendar, Loader2, Users } from 'lucide-react';

// 🟢 Import your existing hooks and APIs! Ensure paths match your project structure.
import { useExpertReviews } from '../../hooks/expertReviewHooks/useExpertReviews';
import { followerAPI } from '../../api/expertFollowerApis/followerApi';

export default function ExpertDashboardReviews() {
  // Assuming the expert's code is stored in localStorage upon login
  const expertCode = localStorage.getItem('expertCode'); 
  
  // ── REVIEWS DATA ──
  const { reviews, ratingStats, isLoadingReviews } = useExpertReviews(expertCode || '');

  const actualReviewsCount = ratingStats?.reviewsCount > 0 ? ratingStats.reviewsCount : (reviews?.length || 0);
  let actualRating = ratingStats?.rating > 0 ? ratingStats.rating : 0;
  if (actualRating === 0 && actualReviewsCount > 0) {
    const sum = reviews.reduce((acc, r) => acc + (Number(r.rating) || 5), 0);
    actualRating = Number((sum / actualReviewsCount).toFixed(1));
  }

  // ── 🟢 FOLLOWERS DATA ──
  const [followers, setFollowers] = useState<any[]>([]);
  const [totalFollowers, setTotalFollowers] = useState(0);
  const [isLoadingFollowers, setIsLoadingFollowers] = useState(true);

  useEffect(() => {
    if (!expertCode) return;

    const fetchFollowersData = async () => {
      setIsLoadingFollowers(true);
      try {
        // Fetch the count
        const countRes = await followerAPI.getFollowerCount(expertCode);
        setTotalFollowers(countRes.count || 0);

        // Fetch the list of people
        const listRes = await followerAPI.getFollowersList(expertCode);
        setFollowers(listRes.followers || []);
      } catch (error) {
        console.error("Failed to load followers:", error);
      } finally {
        setIsLoadingFollowers(false);
      }
    };

    fetchFollowersData();
  }, [expertCode]);

  if (!expertCode) {
    return (
      <div className="p-8 text-center text-red-500 bg-red-50 rounded-xl m-6 border border-red-100">
        Error: Expert session not found. Please log in again.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* ── HEADER ── */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Audience & Reviews</h1>
          <p className="text-sm text-slate-500 mt-1">See what your clients are saying and who is following you.</p>
        </div>

        {/* ── SUMMARY STATS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center shrink-0">
              <Star className="w-7 h-7 fill-amber-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Average Rating</p>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900">{actualRating}</span>
                <span className="text-sm text-slate-500">out of 5</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center shrink-0">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Total Reviews</p>
              <p className="text-3xl font-bold text-slate-900">{actualReviewsCount}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-5">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center shrink-0">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Total Followers</p>
              <p className="text-3xl font-bold text-slate-900">{totalFollowers}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* ── REVIEWS LIST ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-6 border-b border-slate-100 pb-4">Recent Feedback</h2>

            {isLoadingReviews ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3 text-emerald-500" />
                <p>Loading your reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-700">No reviews yet</p>
                <p className="text-sm mt-1">Complete more sessions to start receiving feedback!</p>
              </div>
            ) : (
              // 🟢 FIX: Added max-h-[600px], overflow-y-auto, and pr-2 right here
              <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto pr-2">
                {reviews.map((r, i) => {
                  const reviewerName = r.reviewer_name || 'Anonymous User';
                  const ratingVal = Number(r.rating) || 5;
                  
                  const formattedDate = r.created_at 
                    ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Recent';

                  return (
                    <div key={i} className="flex flex-col bg-slate-50 rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {r.user_photo ? (
                            <img 
                              src={r.user_photo} 
                              alt={reviewerName} 
                              className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm" 
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                              <User className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-bold text-slate-800">{reviewerName}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                              <Calendar className="w-3 h-3" /> {formattedDate}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-0.5 mb-3">
                        {[...Array(5)].map((_, j) => (
                          <Star key={j} className={`w-4 h-4 ${j < ratingVal ? 'fill-amber-400 text-amber-400' : 'fill-slate-200 text-slate-200'}`} />
                        ))}
                      </div>

                      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                        "{r.feedback_text}"
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── 🟢 FOLLOWERS LIST ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-6 border-b border-slate-100 pb-4">Recent Followers</h2>

            {isLoadingFollowers ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Loading your followers...</p>
              </div>
            ) : followers.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-700">No followers yet</p>
                <p className="text-sm mt-1">Keep providing great sessions to grow your audience!</p>
              </div>
            ) : (
              // 🟢 FIX: Added max-h-[600px], overflow-y-auto, and pr-2 right here
              <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto pr-2">
                {followers.map((f, i) => {
                  const followerName = f.follower_name || 'Anonymous User';
                  
                  const formattedDate = f.followed_on 
                    ? new Date(f.followed_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Recent';

                  return (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3">
                        {f.follower_photo ? (
                          <img 
                            src={f.follower_photo} 
                            alt={followerName} 
                            className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm" 
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-bold text-slate-800">{followerName}</p>
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Calendar className="w-3 h-3" /> Followed on {formattedDate}
                          </p>
                        </div>
                      </div>
                      
                      <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shrink-0">
                        Following
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}